import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Loader2, MoreHorizontal, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { membersApi } from '@/api/members';
import { unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import { useAuth } from '@/auth/AuthContext';
import type { MemberInviteInput, MemberInviteResponse, Role } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function MembersTab(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentOrgId, currentOrg } = useWorkspace();
  const { user } = useAuth();
  const callerRole: Role | null = currentOrg?.role ?? null;
  const canManage = callerRole === 'owner' || callerRole === 'admin';

  const [inviteOpen, setInviteOpen] = React.useState(false);

  const membersQuery = useQuery({
    queryKey: ['org', 'members', currentOrgId],
    queryFn: () => membersApi.list(),
    enabled: !!currentOrgId,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['org', 'members', currentOrgId] });
  };

  const setRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      membersApi.setRole(userId, role),
    onSuccess: () => {
      toast.success(t('organization.members.roleUpdated'));
      invalidate();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => membersApi.remove(userId),
    onSuccess: () => {
      toast.success(t('organization.members.removed'));
      invalidate();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const members = membersQuery.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">{t('organization.members.title')}</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(members.length > 1 ? 'organization.members.countMany' : 'organization.members.countOne', { count: members.length })}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setInviteOpen(true)}
          disabled={!canManage}
          className="gap-1.5"
          title={canManage ? t('organization.members.inviteTitle') : t('organization.members.inviteRestricted')}
        >
          <UserPlus className="h-4 w-4" />
          {t('organization.members.invite')}
        </Button>
      </CardHeader>
      <CardContent>
        {membersQuery.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
          </p>
        ) : membersQuery.error ? (
          <p className="text-sm text-destructive">{unwrapError(membersQuery.error)}</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('organization.members.empty')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('organization.members.headers.email')}</TableHead>
                <TableHead>{t('organization.members.headers.role')}</TableHead>
                <TableHead>{t('organization.members.headers.status')}</TableHead>
                <TableHead>{t('organization.members.headers.joined')}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => {
                const isSelf = m.userId === user?.userId;
                const isOwner = m.role === 'owner';
                const ownerCount = members.filter((x) => x.role === 'owner').length;
                const canDemoteOwner = isOwner ? ownerCount > 1 : true;
                const canPromoteToOwner = callerRole === 'owner';
                const canEditThis =
                  canManage && !isSelf && !(isOwner && !canPromoteToOwner);
                const canRemoveThis = canManage && !isSelf && canDemoteOwner;
                return (
                  <TableRow key={m.userId}>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <span>{m.email ?? m.userId}</span>
                        {m.missingUser ? (
                          <Badge variant="destructive" className="text-[10px]">
                            {t('organization.members.userMissing')}
                          </Badge>
                        ) : null}
                        {isSelf ? (
                          <Badge variant="outline" className="text-[10px]">
                            {t('organization.members.me')}
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="uppercase">
                        {m.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {m.enabled === false ? (
                        <Badge variant="destructive" className="text-[10px]">
                          disabled
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">{t('common.active')}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(m.joinedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            disabled={!canEditThis && !canRemoveThis}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEditThis ? (
                            <>
                              <DropdownMenuItem
                                disabled={m.role === 'member'}
                                onClick={() =>
                                  setRoleMutation.mutate({ userId: m.userId, role: 'member' })
                                }
                              >
                                {t('organization.members.demoteMember')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={m.role === 'admin'}
                                onClick={() =>
                                  setRoleMutation.mutate({ userId: m.userId, role: 'admin' })
                                }
                              >
                                {t('organization.members.promoteAdmin')}
                              </DropdownMenuItem>
                              {canPromoteToOwner ? (
                                <DropdownMenuItem
                                  disabled={m.role === 'owner'}
                                  onClick={() => {
                                    if (window.confirm(t('organization.members.transferConfirm', { name: m.email ?? m.userId }))) {
                                      setRoleMutation.mutate({ userId: m.userId, role: 'owner' });
                                    }
                                  }}
                                >
                                  {t('organization.members.transferOwner')}
                                </DropdownMenuItem>
                              ) : null}
                              <DropdownMenuSeparator />
                            </>
                          ) : null}
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            disabled={!canRemoveThis}
                            onClick={() => {
                              if (window.confirm(t('organization.members.removeConfirm', { name: m.email ?? m.userId }))) {
                                removeMutation.mutate(m.userId);
                              }
                            }}
                          >
                            {t('organization.members.removeFromOrg')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={invalidate}
      />
    </Card>
  );
}

function InviteMemberDialog({
  open,
  onOpenChange,
  onInvited,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvited: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<'admin' | 'member'>('member');
  const [displayName, setDisplayName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [result, setResult] = React.useState<MemberInviteResponse | null>(null);

  const reset = (): void => {
    setEmail('');
    setRole('member');
    setDisplayName('');
    setPassword('');
    setResult(null);
  };

  const mutation = useMutation({
    mutationFn: (input: MemberInviteInput) => membersApi.invite(input),
    onSuccess: (data) => {
      setResult(data);
      toast.success(t('organization.members.invitedToast', { email: data.user.email }));
      onInvited();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!email.trim()) return;
    mutation.mutate({
      email: email.trim(),
      role,
      ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      ...(password.trim() ? { password: password.trim() } : {}),
    });
  };

  const close = (): void => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('organization.members.invited')}</DialogTitle>
              <DialogDescription>
                <Trans
                  i18nKey="organization.members.invitedSubtitle"
                  values={{ email: result.user.email, role: result.role }}
                  components={{ b: <strong /> }}
                />
              </DialogDescription>
            </DialogHeader>
            {result.generatedPassword ? (
              <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-50/40 p-3 text-sm dark:bg-amber-950/20">
                <p className="font-medium">{t('organization.members.generatedPasswordTitle')}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all rounded bg-background px-2 py-1 font-mono text-xs">
                    {result.generatedPassword}
                  </code>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      void navigator.clipboard.writeText(result.generatedPassword!);
                      toast.success(t('organization.members.passwordCopied'));
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('organization.members.generatedPasswordHint')}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('organization.members.userExisted')}
              </p>
            )}
            <DialogFooter>
              <Button onClick={close}>{t('common.close')}</Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{t('organization.members.inviteTitle')}</DialogTitle>
              <DialogDescription>{t('organization.members.inviteSubtitle')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="invite-email">{t('organization.members.fields.email')}</Label>
                <Input
                  id="invite-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@acme.com"
                />
              </div>
              <div>
                <Label htmlFor="invite-role">{t('organization.members.fields.role')}</Label>
                <Select value={role} onValueChange={(v) => setRole(v as 'admin' | 'member')}>
                  <SelectTrigger id="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">member</SelectItem>
                    <SelectItem value="admin">admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="invite-display">{t('organization.members.fields.displayName')}</Label>
                <Input
                  id="invite-display"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <Label htmlFor="invite-pw">{t('organization.members.fields.password')}</Label>
                <Input
                  id="invite-pw"
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder=""
                  minLength={8}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('organization.members.passwordHint')}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {t('organization.members.invite')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
